import React, { useCallback, useContext } from 'react';
import { TransitionProps } from '@material-ui/core/transitions';
import { RemoveScroll } from 'react-remove-scroll';
import Zoom from '@material-ui/core/Zoom';
import clsx from 'clsx';

import { ReactComponent as CrossSVG } from 'modules/theme/icons/general/cross.svg';
import { ButtonColour, ButtonStyle } from 'components/Button/constants';
import PlatformContext from 'modules/platform/context';
import { ModalSize } from 'modules/modals/constants';
import { htmlSafe } from 'modules/utils/utils';
import * as Button from 'components/Button';
import ModalOverlay from '../Overlay';

import modalStyles from 'modules/modals/styles.module.scss';
import styles from './styles.module.scss';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Zoom ref={ref} {...props} />;
});

export type ModalConfirmTemplateProps = {
  onCancel: () => void;
  onConfirm: () => void;
  size?: ModalSize;
  text?: string | JSX.Element;
  title: string;
  cancelLabel?: string;
  confirmLabel?: string;
};

export const ModalConfirmTemplate = React.memo<ModalConfirmTemplateProps>(
  ({
    onCancel,
    onConfirm,
    size = ModalSize.NARROW,
    text,
    title,
    cancelLabel,
    confirmLabel,
  }) => {
    const classNames = clsx(modalStyles[`${size}`], styles.modalConfirm);
    const buttonClassNames = clsx(modalStyles.buttons, styles.buttons);
    return (
      <section className={classNames} role="dialog">
        <header>
          <h2>{title}</h2>
          <Button.Generic
            onClick={onCancel}
            ariaLabel="Cancel popup"
            title="Cancel popup"
          >
            <CrossSVG />
          </Button.Generic>
        </header>
        <div>
          {typeof text === 'string' ? <p>{htmlSafe(text)}</p> : text}
          <div className={buttonClassNames}>
            <Button.Generic
              ariaLabel="Cancel popup"
              colour={ButtonColour.BRAND_MONO_ONE}
              label={cancelLabel ? cancelLabel : 'Cancel'}
              onClick={onCancel}
              style={ButtonStyle.SECONDARY}
            />
            <Button.Generic
              ariaLabel="Confirm popup"
              colour={ButtonColour.BRAND_BLACK_GRAPE}
              label={confirmLabel ? confirmLabel : 'Confirm'}
              onClick={onConfirm}
              style={ButtonStyle.SECONDARY}
            />
          </div>
        </div>
      </section>
    );
  },
);

export type ModalConfirmProps = {
  destroyModal: () => void;
  disableRestoreFocus?: boolean;
  hideModal: () => void;
  id: string;
  onCancel: () => void;
  onConfirm: () => void;
  open: boolean;
  size?: ModalSize;
  text?: string | JSX.Element;
  title: string;
  cancelLabel?: string;
  confirmLabel?: string;
};

const Confirm: React.FunctionComponent<ModalConfirmProps> = ({
  destroyModal,
  disableRestoreFocus,
  hideModal,
  onCancel,
  onConfirm,
  open,
  text,
  title,
  cancelLabel,
  confirmLabel,
}) => {
  const { isMobile } = useContext(PlatformContext);

  const handleCancel = useCallback(() => {
    onCancel ? open && onCancel() : open && hideModal();
  }, [hideModal, onCancel, open]);

  const handleConfirm = useCallback(() => {
    onConfirm ? open && onConfirm() : open && hideModal();
  }, [hideModal, onConfirm, open]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancel();
        event.preventDefault();
        event.stopPropagation();
      }
      if (event.key === 'Enter') {
        handleConfirm();
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [handleCancel, handleConfirm],
  );

  return (
    <RemoveScroll enabled={isMobile}>
      <ModalOverlay
        disableRestoreFocus={disableRestoreFocus}
        onExited={destroyModal}
        onKeyDown={handleKeyDown}
        open={open}
        scroll="paper"
        TransitionComponent={Transition}
      >
        <ModalConfirmTemplate
          onCancel={handleCancel}
          onConfirm={onConfirm}
          text={text}
          title={title}
          cancelLabel={cancelLabel}
          confirmLabel={confirmLabel}
        />
      </ModalOverlay>
    </RemoveScroll>
  );
};

export default Confirm;
